import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Alert, Button, Box, Typography } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useRoutingStore } from '../stores/routingStore'
import { isChunkLoadError } from '../utils/chunkLoadError'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  isNetworkError: boolean
  isChunkError: boolean
  autoRecoveryCountdown: number | null
  recoveryAttempts: number
}

// Patterns that indicate a network/WebSocket/Colyseus error (recoverable)
const NETWORK_ERROR_PATTERNS = [
  'WebSocket',
  'websocket',
  'connection',
  'CLOSED',
  'readyState',
  'colyseus',
  'reconnect',
  'ECONNREFUSED',
  'ECONNRESET',
  'ETIMEDOUT',
  'network',
  'NetworkError',
  'Failed to fetch',
  'socket hang up',
]

function isNetworkRelatedError(error: Error): boolean {
  // Chunk load errors should NOT be classified as network errors
  if (isChunkLoadError(error)) return false

  const message = error.message || ''
  const name = error.name || ''
  const stack = error.stack || ''
  const combined = `${message} ${name} ${stack}`
  return NETWORK_ERROR_PATTERNS.some((pattern) =>
    combined.toLowerCase().includes(pattern.toLowerCase())
  )
}

const MAX_RECOVERY_ATTEMPTS = 3

/**
 * AppErrorBoundary - A React error boundary for the main App component
 *
 * Catches errors in the App component tree and provides:
 * - Network error detection with automatic recovery attempt
 * - Global unhandled rejection/error logging
 * - Full-page fallback UI for non-recoverable errors
 */
class AppErrorBoundary extends Component<Props, State> {
  private autoRecoveryTimer: NodeJS.Timeout | null = null
  private countdownTimer: NodeJS.Timeout | null = null
  private chunkReloadTimer: NodeJS.Timeout | null = null
  private lastUnhandledRejectionLog: number = 0
  private readonly REJECTION_LOG_THROTTLE_MS = 2000 // Don't log rejections more than once per 2s

  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isNetworkError: false,
      isChunkError: false,
      autoRecoveryCountdown: null,
      recoveryAttempts: 0,
    }
  }

  componentDidMount() {
    // Catch unhandled promise rejections (e.g., silent Colyseus async failures)
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection)
    // Catch global errors that escape React's boundary
    window.addEventListener('error', this.handleGlobalError)
  }

  componentWillUnmount() {
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection)
    window.removeEventListener('error', this.handleGlobalError)
    if (this.autoRecoveryTimer) clearTimeout(this.autoRecoveryTimer)
    if (this.countdownTimer) clearInterval(this.countdownTimer)
    if (this.chunkReloadTimer) clearTimeout(this.chunkReloadTimer)
  }

  private handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
    // Rate-limit rejection logging to prevent log flooding under heavy errors
    const now = Date.now()
    if (now - this.lastUnhandledRejectionLog < this.REJECTION_LOG_THROTTLE_MS) return
    this.lastUnhandledRejectionLog = now

    const reason = event.reason
    const errorMessage =
      reason instanceof Error ? reason.message : String(reason)

    // Ignore harmless LiveKit client disconnect rejections caused by React effect cleanups
    // (A known issue in livekit-client where canceling a connection leaves an internal promise unhandled)
    if (errorMessage.includes('Client initiated disconnect')) {
      event.preventDefault() // Prevents the browser from logging it as Uncaught (in promise)
      return // Do not log it in the AppErrorBoundary
    }

    console.error('[AppErrorBoundary] Unhandled promise rejection:', errorMessage)
  }

  private handleGlobalError = (event: ErrorEvent): void => {
    console.error('[AppErrorBoundary] Global error:', event.message, event.error)
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const chunkError = isChunkLoadError(error)
    return {
      hasError: true,
      error,
      errorInfo: null,
      isChunkError: chunkError,
      isNetworkError: chunkError ? false : isNetworkRelatedError(error),
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Always log errors (not just in development)
    console.error('[AppErrorBoundary] Caught error:', error)
    console.error('[AppErrorBoundary] Component stack:', errorInfo.componentStack)

    this.setState((prevState) => ({
      errorInfo,
      recoveryAttempts: prevState.recoveryAttempts + 1,
    }))

    // Chunk/module load errors (stale hashes after rebuild/deploy) require a full page reload
    if (isChunkLoadError(error)) {
      console.log('[AppErrorBoundary] Stale chunk detected, reloading page in 2s...')
      this.chunkReloadTimer = setTimeout(() => {
        window.location.reload()
      }, 2000)
      return
    }

    // For network errors, attempt automatic recovery with exponential backoff
    if (isNetworkRelatedError(error)) {
      const attempts = this.state.recoveryAttempts + 1
      if (attempts <= MAX_RECOVERY_ATTEMPTS) {
        // Exponential backoff: 5s, 10s, 20s
        const backoffSeconds = 5 * Math.pow(2, attempts - 1)
        console.log(`[AppErrorBoundary] Network error detected, scheduling auto-recovery in ${backoffSeconds}s (attempt ${attempts}/${MAX_RECOVERY_ATTEMPTS})`)
        this.startAutoRecovery(backoffSeconds)
      } else {
        console.warn(`[AppErrorBoundary] Max recovery attempts (${MAX_RECOVERY_ATTEMPTS}) reached, stopping auto-recovery`)
      }
    }
  }

  private startAutoRecovery(seconds: number): void {
    this.setState({ autoRecoveryCountdown: seconds })

    this.countdownTimer = setInterval(() => {
      this.setState((prevState) => {
        const next = (prevState.autoRecoveryCountdown ?? 1) - 1
        if (next <= 0) {
          if (this.countdownTimer) clearInterval(this.countdownTimer)
          return { autoRecoveryCountdown: 0 }
        }
        return { autoRecoveryCountdown: next }
      })
    }, 1000)

    this.autoRecoveryTimer = setTimeout(() => {
      console.log('[AppErrorBoundary] Attempting automatic recovery')
      this.handleRetry()
    }, seconds * 1000)
  }

  handleRetry = (): void => {
    if (this.autoRecoveryTimer) clearTimeout(this.autoRecoveryTimer)
    if (this.countdownTimer) clearInterval(this.countdownTimer)
    if (this.chunkReloadTimer) clearTimeout(this.chunkReloadTimer)

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isNetworkError: false,
      isChunkError: false,
      autoRecoveryCountdown: null,
      // Note: recoveryAttempts is intentionally preserved across retries
    })

    // Dispatch a custom event so the Network layer can attempt reconnection
    window.dispatchEvent(new CustomEvent('app-error-boundary-recovery'))
  }

  handleReload = (): void => {
    if (this.autoRecoveryTimer) clearTimeout(this.autoRecoveryTimer)
    if (this.countdownTimer) clearInterval(this.countdownTimer)
    if (this.chunkReloadTimer) clearTimeout(this.chunkReloadTimer)

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isNetworkError: false,
      isChunkError: false,
      autoRecoveryCountdown: null,
      recoveryAttempts: 0,
    })

    try {
      useRoutingStore.getState().navigateTo('/', true)
    } catch (navError) {
      // If routing fails (store may be corrupted), fall back to hard reload
      console.error('[AppErrorBoundary] Navigation failed, falling back to hard reload:', navError)
      window.location.href = '/'
    }
  }

  private getErrorTitle(): string {
    if (this.state.isChunkError) return 'App Updated'
    if (this.state.isNetworkError) return 'Connection Issue'
    return 'Application Error'
  }

  private getErrorMessage(): string {
    if (this.state.isChunkError) {
      return 'A new version of the app is available. Reloading automatically...'
    }
    if (this.state.isNetworkError) {
      if (this.state.recoveryAttempts > MAX_RECOVERY_ATTEMPTS) {
        return 'A network issue was detected. Automatic recovery failed after multiple attempts. Please reload the page.'
      }
      return 'A network issue was detected. Attempting to reconnect automatically...'
    }
    return 'An unexpected error occurred. Please try again or reload the page.'
  }

  private getAlertSeverity(): 'info' | 'warning' | 'error' {
    if (this.state.isChunkError) return 'info'
    if (this.state.isNetworkError) return 'warning'
    return 'error'
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 9999,
            padding: 3,
          }}
        >
          <Alert
            severity={this.getAlertSeverity()}
            sx={{
              maxWidth: 600,
              width: '100%',
            }}
            action={
              !this.state.isChunkError ? (
                <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                  {this.state.recoveryAttempts <= MAX_RECOVERY_ATTEMPTS && (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={this.handleRetry}
                      startIcon={<RefreshIcon />}
                    >
                      {this.state.isNetworkError ? 'Reconnect Now' : 'Retry'}
                    </Button>
                  )}
                  <Button
                    color="inherit"
                    size="small"
                    onClick={this.handleReload}
                    variant="outlined"
                  >
                    Reload Page
                  </Button>
                </Box>
              ) : undefined
            }
          >
            <Typography variant="h6" gutterBottom>
              {this.getErrorTitle()}
            </Typography>
            <Typography variant="body2" gutterBottom>
              {this.getErrorMessage()}
            </Typography>
            {this.state.autoRecoveryCountdown !== null &&
              this.state.autoRecoveryCountdown > 0 && (
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  Auto-recovering in {this.state.autoRecoveryCountdown}s...
                </Typography>
              )}
            {this.state.error && !this.state.isChunkError && (
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  marginTop: 2,
                  padding: 1,
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  overflow: 'auto',
                  maxHeight: 200,
                }}
              >
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack && (
                  <>
                    {'\n\nComponent Stack:\n'}
                    {this.state.errorInfo.componentStack}
                  </>
                )}
              </Typography>
            )}
          </Alert>
        </Box>
      )
    }

    // If no error, render children normally
    return this.props.children
  }
}

export default AppErrorBoundary
